<template>
  <div v-if="banner" class="legacy-touch" :class="`legacy-touch--${banner}`" role="status">
    <EnterpriseIcon :src="icon" class="legacy-touch__icon" />
    <span class="legacy-touch__text">{{ bannerText }}</span>
    <button v-if="banner !== 'PROCESSING' || state?.owner" type="button" class="legacy-touch__action" @click="openBanner">{{ actionText }}</button>
  </div>
  <OwnerMigrationDialog
    v-if="state?.owner && ownerVisible" :key="sessionRevision" v-model="ownerVisible" :state="state"
    @refresh="refreshAfterAction"
  />
  <el-dialog v-model="infoVisible" class="legacy-touch-dialog" :class="`legacy-touch-dialog--${infoKind}`" :width="infoKind === 'D' ? '460px' : '560px'"
    align-center append-to-body :close-on-click-modal="false" :show-close="true" destroy-on-close>
    <template v-if="infoKind === 'B'">
      <h2>{{ migrationT('touch_b_title') }}</h2>
      <div class="legacy-touch-dialog__date-chip">
        <EnterpriseIcon :src="calendarIcon" class="legacy-touch-dialog__date-icon" />
        <span>{{ migrationT('touch_b_date_chip', { deadline }) }}</span>
      </div>
      <p>{{ migrationT('touch_b_intro') }}</p>
      <h3>{{ migrationT('touch_b_result_title') }}</h3>
      <div class="legacy-touch-dialog__rows">
        <section>
          <EnterpriseIcon :src="globeIcon" class="legacy-touch-dialog__row-icon" />
          <div>
            <strong>{{ $t('legacyOwner_region_ai_only') }}</strong>
            <p>{{ migrationT('touch_b_ai_result') }}</p>
          </div>
        </section>
        <section>
          <EnterpriseIcon :src="mapPinIcon" class="legacy-touch-dialog__row-icon" />
          <div>
            <strong>{{ $t('legacyOwner_region_vip_only') }}</strong>
            <p>{{ migrationT('touch_b_vip_result') }}</p>
          </div>
        </section>
        <section>
          <EnterpriseIcon :src="switchIcon" class="legacy-touch-dialog__row-icon" />
          <div>
            <strong>{{ $t('legacyOwner_region_cross_region') }}</strong>
            <p>{{ migrationT('touch_b_cross_result') }}</p>
          </div>
        </section>
      </div>
      <div class="legacy-touch-dialog__divider" />
      <p class="legacy-touch-dialog__footnote">{{ migrationT('touch_b_footnote') }}</p>
      <p v-if="state?.ownerEmail" class="legacy-touch-dialog__muted">{{ migrationT('admin_label', { email: state.ownerEmail }) }}</p>
      <button class="legacy-touch-dialog__primary" @click="infoVisible = false">{{ $t('iKnow') }}</button>
    </template>
    <template v-else-if="infoKind === 'C'">
      <h2>{{ migrationT('touch_c_title') }}</h2>
      <div class="legacy-touch-dialog__notice legacy-touch-dialog__notice--warning">
        <svg class="legacy-touch-dialog__notice-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
        <span>{{ migrationT('touch_c_deadline_notice', { deadline }) }}</span>
      </div>
      <ol class="legacy-touch-dialog__steps">
        <li>
          <span class="legacy-touch-dialog__step-index">1</span>
          <div>
            <strong>{{ migrationT('touch_c_step1_title') }}</strong>
            <p>{{ migrationT('touch_c_step1_desc') }}</p>
          </div>
        </li>
        <li>
          <span class="legacy-touch-dialog__step-index">2</span>
          <div>
            <strong>{{ migrationT('touch_c_step2_title') }}</strong>
            <p>{{ migrationT('touch_c_step2_desc') }}</p>
          </div>
        </li>
        <li>
          <span class="legacy-touch-dialog__step-index">3</span>
          <div>
            <strong>{{ migrationT('touch_c_step3_title') }}</strong>
            <p>{{ migrationT('touch_c_step3_desc') }}</p>
          </div>
        </li>
      </ol>
      <p class="legacy-touch-dialog__muted">{{ migrationT('touch_c_missing_email_tip', { email: state?.ownerEmail || '' }) }}</p>
      <p v-if="siteLoading" role="status">{{ migrationT('touch_c_loading_site') }}</p>
      <a v-else-if="counterpartUrl" class="legacy-touch-dialog__primary" :href="counterpartUrl" target="_blank" rel="noopener noreferrer">{{ migrationT('touch_c_go_register') }}</a>
      <p v-else role="alert">{{ migrationT('touch_c_site_missing') }}</p>
    </template>
    <template v-else-if="infoKind === 'D'">
      <h2>{{ migrationT('touch_d_title') }}</h2>
      <p v-if="inviteLoading" role="status">{{ migrationT('touch_d_loading_invite') }}</p>
      <template v-else-if="inviteUrl">
        <div class="legacy-touch-dialog__link"><input :aria-label="migrationT('touch_d_invite_link_aria')" :value="inviteUrl" readonly /><button type="button" @click="copyInvite">{{ copied ? migrationT('copied') : $t('enterprise_management_copy') }}</button></div>
        <div class="legacy-touch-dialog__divider" />
        <p v-if="invite?.expireTime" class="legacy-touch-dialog__muted legacy-touch-dialog__muted--small">{{ migrationT('valid_until', { time: formatDateTime(invite.expireTime) }) }}</p>
        <p class="legacy-touch-dialog__muted">{{ migrationT('touch_d_send_link_tip') }}</p>
      </template>
      <p v-else role="alert">{{ inviteError || migrationT('invite_unavailable_retry') }}</p>
      <p v-if="inviteUrl && inviteError" role="alert">{{ inviteError }}</p>
      <button class="legacy-touch-dialog__primary" @click="infoVisible = false">{{ $t('legacyOwner_success_done') }}</button>
    </template>
    <template v-else>
      <h2>{{ migrationT('touch_default_title') }}</h2>
      <p>{{ migrationT('touch_default_desc') }}</p>
      <button class="legacy-touch-dialog__primary" @click="infoVisible = false">{{ $t('iKnow') }}</button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import useUserStore from '~/store/modules/user'
import { useSiteConfig } from '~/config/useSiteConfig'
import LegacyServer from '~/services/LegacyEnterpriseServer'
import AccountServer from '~/services/AccountServer'
import { trustedEnterpriseWebUrl } from '~/utils/enterpriseWebLinks'
import type { LegacyTouchState, LegacyInvite } from '~/services/LegacyEnterpriseServer/types'
import { createLegacyTouchLoader, legacyBanner } from '~/utils/legacyEnterpriseTouch'
import EnterpriseIcon from '~/components/Index/enterprise/EnterpriseIcon.vue'
import OwnerMigrationDialog from './OwnerMigrationDialog.vue'
import warningIcon from '~/assets/images/enterprise/icons/triangle-alert.svg'
import infoIcon from '~/assets/images/enterprise/icons/info.svg'
import successIcon from '~/assets/images/enterprise/icons/circle-check.svg'
import calendarIcon from '~/assets/images/enterprise/icons/calendar.svg'
import globeIcon from '~/assets/images/enterprise/icons/globe.svg'
import mapPinIcon from '~/assets/images/enterprise/icons/map-pin.svg'
import switchIcon from '~/assets/images/enterprise/icons/arrow-left-right.svg'
import { useLegacyEnterpriseWorkspace } from '~/composables/useLegacyEnterpriseWorkspace'
import { legacyWorkspaceQuota } from '~/utils/legacyEnterpriseWorkspace'

const user = useUserStore()
const { t, locale } = useI18n()
const { isAiSite } = useSiteConfig()
const route = useRoute()
const state = ref<LegacyTouchState | null>(null)
const ownerVisible = ref(false), infoVisible = ref(false), infoKind = ref('B'), sessionRevision = ref(0)
const invite = ref<LegacyInvite | null>(null), inviteLoading = ref(false), inviteError = ref(''), copied = ref(false)
const counterpartUrl = ref(''), siteLoading = ref(false)
const banner = computed(() => legacyBanner(state.value))
const workspace = useLegacyEnterpriseWorkspace(computed(() => banner.value === 'D'))
const deadline = computed(() => localDeadlineLabel(state.value?.deadline))
const icon = computed(() => banner.value === 'D' ? successIcon : ['B', 'PROCESSING'].includes(banner.value) ? infoIcon : warningIcon)
const bannerText = computed(() => {
  switch (banner.value) {
    case 'A': return migrationT('banner_a', { deadline: deadline.value, days: state.value?.daysRemaining })
    case 'B': return migrationT('banner_b', { deadline: deadline.value })
    case 'C': return migrationT('banner_c', { deadline: deadline.value })
    case 'D': return chinaPoolBannerText.value
    case 'PROCESSING': return migrationT('banner_processing')
    default: return ''
  }
})
const actionText = computed(() => ({
  A: migrationT('action_confirm_now'),
  B: migrationT('action_learn_more'),
  C: migrationT('action_view_guide'),
  D: migrationT('action_view_invite'),
  PROCESSING: migrationT('action_view_progress'),
})[banner.value])
const inviteUrl = computed(() => invite.value?.status === 'ACTIVE' ? trustedEnterpriseWebUrl(invite.value.url) : '')
const localeForIntl = computed(() => {
  const value = String(locale.value || 'zh')
  if (value === 'tw') return 'zh-TW'
  if (value === 'itb') return 'it'
  if (value === 'zh') return 'zh-CN'
  return value
})
const chinaPoolBannerText = computed(() => {
  const words = legacyWorkspaceQuota(workspace.summary.value, 'VIP')
  if (words !== null) return migrationT('banner_d_ready', { count: formatWords(words) })
  return migrationT('banner_d_balance', { status: workspace.loading.value ? migrationT('loading') : migrationT('unavailable') })
})
const loader = createLegacyTouchLoader(LegacyServer, value => {
  if (infoVisible.value && infoKind.value !== 'DEFAULT' && legacyBanner(value) !== infoKind.value) infoVisible.value = false
  state.value = value
  if (!value) { ownerVisible.value = false; infoVisible.value = false }
}, value => {
  if (value.prompt === 'DEFAULT_INTERNATIONAL') { infoKind.value = 'DEFAULT'; infoVisible.value = true }
  else ownerVisible.value = true
})
async function refreshAfterAction() { await loader.refresh() }

function migrationT(key: string, params?: Record<string, unknown>) {
  return t(`enterprise_ui_migration_${key}`, params || {})
}

function localDeadlineLabel(deadline?: string) {
  if (!deadline || !/(Z|[+-]\d{2}:\d{2})$/.test(deadline)) return ''
  const time = Date.parse(deadline)
  if (!Number.isFinite(time)) return ''
  return new Intl.DateTimeFormat(localeForIntl.value, {
    timeZone: 'Asia/Shanghai',
    month: 'short',
    day: 'numeric',
  }).format(new Date(time - 1))
}

function formatDateTime(value: string) {
  const date = new Date(value)
  if (!Number.isFinite(date.getTime())) return value
  return new Intl.DateTimeFormat(localeForIntl.value, {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

function formatWords(value: unknown) {
  try { return BigInt(String(value)).toLocaleString(localeForIntl.value) } catch { return '—' }
}

async function openBanner() {
  if (banner.value === 'A' || banner.value === 'PROCESSING') { ownerVisible.value = true; return }
  infoKind.value = banner.value; infoVisible.value = true
  if (banner.value === 'C') {
    const revision = sessionRevision.value
    siteLoading.value = true; counterpartUrl.value = ''
    try {
      const result = await AccountServer.getEnterpriseWebSites()
      if (revision === sessionRevision.value && result.code === '1') counterpartUrl.value = trustedEnterpriseWebUrl(result.data?.VIP)
    } catch { /* 保留明确的缺配置提示，不回退其他站点。 */ }
    finally { if (revision === sessionRevision.value) siteLoading.value = false }
    return
  }
  if (banner.value !== 'D' || !state.value?.migrationNo) return
  const revision = sessionRevision.value, migrationNo = state.value.migrationNo
  invite.value = null; inviteError.value = ''; copied.value = false; inviteLoading.value = true
  try {
    const result = await LegacyServer.invite(migrationNo)
    if (revision !== sessionRevision.value || state.value?.migrationNo !== migrationNo) return
    if (result.code === '1' && result.data) invite.value = result.data
    else inviteError.value = result.message || migrationT('invite_unavailable')
  } catch { if (revision === sessionRevision.value) inviteError.value = migrationT('invite_load_failed') }
  finally { if (revision === sessionRevision.value) inviteLoading.value = false }
}
async function copyInvite() {
  if (!inviteUrl.value) return
  try { await navigator.clipboard.writeText(inviteUrl.value); copied.value = true }
  catch { inviteError.value = migrationT('copy_failed_select_manually') }
}
let stop: (() => void) | undefined, timer: ReturnType<typeof setInterval> | undefined
const refreshVisible = () => { if (document.visibilityState === 'visible' && !ownerVisible.value) void loader.refresh() }
const refreshVisibleWorkspace = () => {
  refreshVisible()
  // Refresh live quota on explicit navigation/focus, not the touch-state polling timer.
  if (document.visibilityState === 'visible' && banner.value === 'D') void workspace.refresh()
}
onMounted(() => {
  stop = watch(() => isAiSite.value && user.token && user.isUserInfoReady && user.userInfo?.bizTeamId
    ? `${user.token}:${user.userInfo.id}:${user.userInfo.bizTeamId}:${user.userInfo.bizTeamMemberRole}` : '', identity => {
    sessionRevision.value++; ownerVisible.value = false; infoVisible.value = false; invite.value = null; counterpartUrl.value = ''; siteLoading.value = false
    loader.reset(identity); void loader.refresh()
  }, { immediate: true, flush: 'sync' })
  timer = setInterval(refreshVisible, 30000)
  document.addEventListener('visibilitychange', refreshVisibleWorkspace)
  window.addEventListener('focus', refreshVisibleWorkspace)
})
watch(() => route.fullPath, () => { if (import.meta.client) refreshVisibleWorkspace() })
onBeforeUnmount(() => {
  stop?.(); if (timer) clearInterval(timer); loader.dispose()
  document.removeEventListener('visibilitychange', refreshVisibleWorkspace); window.removeEventListener('focus', refreshVisibleWorkspace)
})
</script>

<style lang="scss">
.legacy-touch { display:flex; align-items:center; gap:10px; padding:10px 16px; border:1px solid #FDE68A; border-radius:8px; background:#FFF7E6; color:#92400E; flex-shrink:0;
  &__icon { width:16px!important; height:16px!important; flex:0 0 16px; color:#D97706; }
  &__text { flex:1; min-width:0; font-size:13px; line-height:1.55; font-weight:600; }
  &__action { border:0; border-radius:6px; background:#2B7DE9; padding:6px 14px; color:#fff; white-space:nowrap; font-size:12.5px; font-weight:600; cursor:pointer; }
  &--B,&--PROCESSING { background:#EAF3FF; border-color:#D6E7FC; color:#51565D; .legacy-touch__icon{color:#2B7DE9} .legacy-touch__text{font-weight:400} }
  &--D { background:#ECFDF3; border-color:#A7F3D0; color:#166534; .legacy-touch__icon{color:#00B578} }
  &--B,&--D { .legacy-touch__action{background:transparent;color:#2B7DE9} }
}
.el-dialog.legacy-touch-dialog { box-sizing:border-box; width:var(--el-dialog-width,560px)!important; max-width:calc(100vw - 32px); padding:0!important; border-radius:14px!important; box-shadow:0 16px 44px #0A234D33; max-height:calc(100dvh - 32px); overflow:hidden;
  .el-dialog__header{padding:0!important;margin:0!important;height:0}.el-dialog__headerbtn{right:22px!important;top:22px!important;width:24px;height:24px}.el-dialog__body{box-sizing:border-box;max-height:calc(100dvh - 32px);overflow-y:auto;padding:28px!important;display:flex;flex-direction:column;gap:14px;color:#51565D;font-size:13px;line-height:1.6;word-break:normal}
  .el-dialog__body > * { flex-shrink:0; }
  h2{font-size:18px;font-weight:800;color:#1F2329;line-height:1.4;padding-right:24px;margin:0}h3,strong{font-size:13px;font-weight:700;color:#1F2329;margin:0}p{margin:0}
  .legacy-touch-dialog__date-chip{display:flex;align-items:center;gap:6px;align-self:flex-start;width:fit-content;border-radius:8px;background:#EAF3FF;padding:7px 12px;color:#2B7DE9;font-size:12.5px;line-height:1.4;font-weight:700}
  .legacy-touch-dialog__date-icon{width:14px!important;height:14px!important;flex:0 0 14px;color:#2B7DE9}
  .legacy-touch-dialog__notice{display:flex;align-items:center;gap:8px;border-radius:8px;padding:9px 12px;font-size:12.5px;line-height:1.5;font-weight:600}
  .legacy-touch-dialog__notice--warning{border:1px solid #FDE68A;background:#FFF7E6;color:#92400E}
  .legacy-touch-dialog__notice-icon{width:14px!important;height:14px!important;flex:0 0 14px;color:#D97706}
  .legacy-touch-dialog__muted{color:#8F959E;font-size:12px;line-height:1.6}
  .legacy-touch-dialog__muted--small{font-size:11.5px}
  .legacy-touch-dialog__footnote{color:#8F959E;font-size:12.5px;line-height:1.6}
  .legacy-touch-dialog__divider{height:1px;background:#EEF1F5}
  .legacy-touch-dialog__rows{display:grid;gap:10px;section{display:flex;gap:10px;align-items:flex-start;background:transparent;border-radius:0;padding:0}strong{display:block;font-size:13px;line-height:1.5}p{font-size:12.5px;line-height:1.6;color:#51565D}}
  .legacy-touch-dialog__row-icon{width:16px!important;height:16px!important;flex:0 0 16px;margin-top:2px;color:#2B7DE9}
  .legacy-touch-dialog__steps{list-style:none;padding:0;margin:0;display:grid;gap:14px;li{display:flex;gap:10px;align-items:flex-start}strong{display:block;font-size:13.5px;line-height:1.5}p{font-size:12.5px;line-height:1.6;color:#51565D}}
  .legacy-touch-dialog__step-index{display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;flex:0 0 22px;border-radius:50%;background:#2B7DE9;color:#fff;font-size:11.5px;line-height:1;font-weight:700}
  .legacy-touch-dialog__primary{display:flex;align-items:center;justify-content:center;min-height:40px;width:100%;box-sizing:border-box;text-decoration:none;border:0;border-radius:8px;background:#2B7DE9;color:#fff;font-size:13.5px;font-weight:600;cursor:pointer}
  .legacy-touch-dialog__link{display:flex;gap:10px;input{flex:1;min-width:0;height:38px;box-sizing:border-box;border:1px solid #D9DEE5;border-radius:8px;padding:0 12px;background:#F7F9FC;color:#51565D;font-size:12.5px}button{height:38px;border:0;border-radius:8px;background:#2B7DE9;color:#fff;padding:0 16px;font-size:13px;font-weight:600;cursor:pointer}}
  &.legacy-touch-dialog--D h2{font-size:17px}
}
@media(max-width:600px){.legacy-touch{flex-wrap:wrap;.legacy-touch__action{margin-left:26px}}}
</style>
